import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {Stack} from '@primer/react'
import {useSlots} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {createContext, type PropsWithChildren, use, useCallback, useMemo, useState} from 'react'

import styles from './styles.module.css'

type PayloadType = 'nav_menu' | 'user_menu' | 'create_menu' | 'agents_menu' | 'account_switch_dialog'

const DEFAULT_PAYLOADS_URL = '/_global-navigation/payloads.json'

export type {PayloadType}

/**
 * Stable configuration that does not change in response to user interaction. Split out from the
 * dynamic state (below) so that consumers of these values don't re-render when search open/hidden
 * state toggles.
 */
type TopNavConfigContextValue = {
  hasLocalNavigation: boolean
  isSearchPage: boolean
  setQuickSearchOpen: (open: boolean) => void
  setTopNavHidden: (hidden: boolean) => void
  loadCopilotChat: () => void
  getPayloadsUrl: (type: PayloadType) => string
  payloadsBaseUrl: string
}

/**
 * Search-driven UI state that toggles in response to user interaction. Kept in its own context so a
 * change here only re-renders the components that actually read it (Search, the breadcrumb context
 * region, and the header itself).
 */
type TopNavSearchStateContextValue = {
  quickSearchOpen: boolean
  topNavHidden: boolean
}

/**
 * Deferred copilot-chat load state. Isolated in its own context because it flips on the first
 * hover/focus of the search or copilot controls — an early, common interaction — and is read by
 * exactly one component (`CopilotItems`). Bundling it with the search state would needlessly
 * re-render the header parent and breadcrumbs on every such hover.
 */
type CopilotChatLoadContextValue = {
  shouldLoadCopilotChat: boolean
}

const TopNavConfigContext = createContext<TopNavConfigContextValue>({
  hasLocalNavigation: false,
  isSearchPage: false,
  setQuickSearchOpen: () => {},
  setTopNavHidden: () => {},
  loadCopilotChat: () => {},
  getPayloadsUrl: (type: PayloadType) => `/_global-navigation/payloads.json?type=${type}`,
  payloadsBaseUrl: DEFAULT_PAYLOADS_URL,
})

const TopNavSearchStateContext = createContext<TopNavSearchStateContextValue>({
  quickSearchOpen: false,
  topNavHidden: false,
})

const CopilotChatLoadContext = createContext<CopilotChatLoadContextValue>({
  shouldLoadCopilotChat: false,
})

export type TopNavProps = React.PropsWithChildren<{
  /** Whether the navigation has local navigation tabs below the main bar */
  hasLocalNavigation: boolean
  /** Whether the search input is on the search page */
  isSearchPage: boolean
  /** Whether the search input is a "jump to" search */
  isJumpToSearch: boolean
  /** URL for fetching payload data */
  payloadsUrl?: string | null
}>

export function shouldHideTopNav(urlParams: URLSearchParams, isSearchPage: boolean, isJumpToSearch: boolean) {
  const shouldHide = isSearchPage && !isJumpToSearch && !urlParams.get('q')?.trim()

  return shouldHide
}

export function TopNavProvider({
  children,
  hasLocalNavigation,
  isSearchPage,
  isJumpToSearch,
  payloadsUrl: payloadsUrlProp,
}: TopNavProps) {
  // Fall back when the prop is missing *or* explicitly null. A default parameter only applies to
  // `undefined`, but this value can arrive as `null` from a server-provided payload, which would
  // otherwise blow up the `new URL(...)` call in `getPayloadsUrl`.
  const payloadsUrl = payloadsUrlProp ?? DEFAULT_PAYLOADS_URL
  // Defaulting to false so we don't load chat on every page.
  const [shouldLoadCopilotChat, setShouldLoadCopilotChat] = useState(false)
  const [quickSearchOpen, setQuickSearchOpen] = useState(false)
  const [topNavHidden, setTopNavHidden] = useState(() => {
    const urlParams = new URLSearchParams(ssrSafeLocation.search)
    // This can be overridden later via the context setter,
    // but set the default here based on URL params so we don't flash the nav on initial load
    return shouldHideTopNav(urlParams, isSearchPage, isJumpToSearch)
  })

  const loadCopilotChat = useCallback(() => {
    setShouldLoadCopilotChat(true)
  }, [])

  const getPayloadsUrl = useCallback(
    (type: PayloadType) => {
      const url = new URL(payloadsUrl, ssrSafeLocation.origin)
      url.searchParams.set('type', type)
      return url.toString()
    },
    [payloadsUrl],
  )

  // Split context by update frequency *and* read pattern. The config context holds values that are
  // stable for the lifetime of the nav (props + memoized callbacks). The remaining state is
  // partitioned so a change only re-renders components that read it:
  // - search state (`quickSearchOpen`/`topNavHidden`) is read by Search and the header.
  // - copilot-load state (`shouldLoadCopilotChat`) is read only by CopilotItems, but flips on the
  //   first hover/focus of search or copilot — isolating it keeps that frequent early interaction
  //   from re-rendering the header parent and breadcrumbs.
  const configValue = useMemo<TopNavConfigContextValue>(
    () => ({
      hasLocalNavigation,
      isSearchPage,
      setQuickSearchOpen,
      setTopNavHidden,
      loadCopilotChat,
      getPayloadsUrl,
      payloadsBaseUrl: payloadsUrl,
    }),
    [hasLocalNavigation, isSearchPage, loadCopilotChat, getPayloadsUrl, payloadsUrl],
  )

  const searchStateValue = useMemo<TopNavSearchStateContextValue>(
    () => ({quickSearchOpen, topNavHidden}),
    [quickSearchOpen, topNavHidden],
  )

  const copilotLoadValue = useMemo<CopilotChatLoadContextValue>(
    () => ({shouldLoadCopilotChat}),
    [shouldLoadCopilotChat],
  )

  return (
    <TopNavConfigContext value={configValue}>
      <TopNavSearchStateContext value={searchStateValue}>
        <CopilotChatLoadContext value={copilotLoadValue}>{children}</CopilotChatLoadContext>
      </TopNavSearchStateContext>
    </TopNavConfigContext>
  )
}

/**
 * Access the stable TopNav configuration (props and memoized callbacks that don't change in
 * response to user interaction): `hasLocalNavigation`, `isSearchPage`, `setQuickSearchOpen`,
 * `setTopNavHidden`, `loadCopilotChat`, `getPayloadsUrl`, and `payloadsBaseUrl`.
 *
 * Prefer this over `useTopNavSearchState` when you only need configuration/setters, so the
 * component doesn't re-render when search open/hidden state toggles.
 *
 * Should be used within components that are descendants of `TopNavProvider` or `TopNav`.
 */
export function useTopNavConfig() {
  return use(TopNavConfigContext)
}

/**
 * Access the search-driven TopNav UI state: `quickSearchOpen` and `topNavHidden`.
 *
 * Should be used within components that are descendants of `TopNavProvider` or `TopNav`.
 */
export function useTopNavSearchState() {
  return use(TopNavSearchStateContext)
}

/**
 * Access whether the deferred Copilot Chat assets should be loaded. Isolated from the rest of the
 * TopNav state so toggling it (on hover/focus of search or copilot) only re-renders its single
 * consumer rather than the whole nav.
 *
 * Should be used within components that are descendants of `TopNavProvider` or `TopNav`.
 */
export function useShouldLoadCopilotChat() {
  return use(CopilotChatLoadContext).shouldLoadCopilotChat
}

function TopNavComponent({
  children,
  className,
}: PropsWithChildren<{
  className?: string
}>) {
  const {topNavHidden} = useTopNavSearchState()

  const [{left, center, right, above}, rest] = useSlots(children, {
    left: Left,
    center: Center,
    right: Right,
    above: Above,
  })

  return (
    <Stack
      as="header"
      className={clsx(
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        'GlobalNav',
        {
          'd-none': topNavHidden,
        },
        styles.appHeader,
        className,
      )}
      role="banner"
      aria-label="Global navigation menu"
      gap="none"
    >
      {above}
      <Stack direction="horizontal" align="center" justify="center">
        {left}
        {center}
        {right}
      </Stack>
      {rest}
    </Stack>
  )
}

function Left({children}: React.PropsWithChildren) {
  const {hasLocalNavigation} = useTopNavConfig()
  return (
    <Stack
      data-testid="top-nav-left"
      direction="horizontal"
      className={clsx(styles.left, hasLocalNavigation && styles.withLocalNavigation)}
      gap="condensed"
      padding="normal"
    >
      {children}
    </Stack>
  )
}

function Center({children, className}: React.PropsWithChildren<{className?: string}>) {
  const {hasLocalNavigation} = useTopNavConfig()
  return (
    <Stack
      data-testid="top-nav-center"
      direction="horizontal"
      gap="condensed"
      padding="normal"
      className={clsx(styles.center, hasLocalNavigation && styles.withLocalNavigation, className)}
    >
      {children}
    </Stack>
  )
}

function Right({children}: React.PropsWithChildren) {
  const {hasLocalNavigation} = useTopNavConfig()
  // The create menu button is hidden from the bar at narrow widths; dropping the right cluster's left
  // padding there removes the awkward gap it leaves behind.
  return (
    <Stack
      data-testid="top-nav-right"
      direction="horizontal"
      align="center"
      gap="condensed"
      padding="normal"
      className={clsx(
        styles.right,
        hasLocalNavigation && styles.withLocalNavigation,
        styles.rightWithResponsiveCreateButton,
      )}
    >
      {children}
    </Stack>
  )
}

function Above({children}: React.PropsWithChildren) {
  return <>{children}</>
}

/**
 * The TopNav component lays the structure for the top navigation bar within product.
 * Use the `TopNav.Above`, `TopNav.Left`, `TopNav.Center`, and `TopNav.Right` slots to define the content above, left, center, and right sides of the navigation bar.
 * - `TopNav.Above`: Content rendered above the main navigation bar (e.g., banners, alerts).
 * - `TopNav.Left`, `TopNav.Center`, `TopNav.Right`: Content rendered on the left, center, and right of the navigation bar, respectively.
 * Any additional children will be shown below the initial bar (such as Local Navigation).
 */
export const TopNav = Object.assign(TopNavComponent, {Above, Left, Center, Right})
